/** 신상품 등록을 지원하는 게임(2026-08-18 도입, 홀로라이브는 2026-08-21 추가 —
 * pricehub가 그새 hololive_jp API를 갖추면서 더 이상 범위 밖이 아니게 됨). */
export type RegistrableGameType = 'POKEMON' | 'ONE_PIECE' | 'DIGIMON' | 'HOLOLIVE';

export const GAME_TYPE_LABEL: Record<RegistrableGameType, string> = {
  POKEMON:   '포켓몬',
  ONE_PIECE: '원피스',
  DIGIMON:   '디지몬',
  HOLOLIVE:  '홀로라이브',
};

/**
 * 신규 등록 상품의 자리표시 가격·초기 재고 — 백엔드 ProductRegistrationService와 일치시킨다
 * (buildOriginProduct의 salePrice 9990, DEFAULT_PLACEHOLDER_STOCK 1). 판매중지 상태로
 * 생성되므로 고객에게 노출되지 않는 자리표시자이고, 실제 값은 이후 가격 조회/수동 반영으로
 * 채운다. (기존 화면은 재고 0을 안내했으나 기능 정의·백엔드 동작은 1이라 통일 —
 * 2026-08-31, "상품 등록 개선안".)
 */
export const REGISTER_PLACEHOLDER_PRICE = 9990;
export const REGISTER_INIT_STOCK = 1;

/** 부스터팩/스타터덱 — PriceHub Expansion 모델의 product_type 그대로. */
export type ExpansionProductType = 'BOOSTER' | 'STARTER';

/** 신상품 등록 대상으로 고를 수 있는 확장팩 — PriceHub 카탈로그 기준. */
export interface ExpansionOption {
  code:        string;
  name:        string;
  /** 홀로라이브 전용 — 이 게임만 name이 일본어라 드롭다운엔 이 필드를 우선 쓴다. */
  nameKr:      string | null;
  cardCount:   number | null;
  productType: ExpansionProductType;
  releaseDate: string | null;
  /** 로컬 Card 테이블 기준 이 확장팩의 등록 건수 — 드롭다운 "미등록 N장 / 전부 등록됨"
   *  배지용(2026-08-31, "상품 등록 개선안"). ImportProductsJob 캐시라 최대 하루 늦을 수
   *  있음 — 정확한 신규/기존 내역은 확장팩 선택 후 프리뷰가 보여준다. */
  registeredCount: number;
}

/** PriceHub 카드 한 장 — 게임마다 쓰는 플래그가 다르다(안 쓰는 게임은 전부 false). */
export interface RegistrationCandidate {
  shopProductCode:  string;
  cardNumber:       string;
  name:             string;
  /** 홀로라이브 전용 — 이 게임만 name이 일본어라 실제 상품명엔 이 필드를 쓴다. */
  nameKr:           string | null;
  rarity:           string;
  imageUrl:         string | null;
  isParallel:       boolean;
  isManga:          boolean;
  isRedmanga:        boolean;
  isSpecial:        boolean;
  isScarce:         boolean;
  needsRarityCheck: boolean;
  expansionCode:    string;
  expansionName:    string;
  /** 홀로라이브 전용. */
  expansionNameKr:  string | null;
}

/** "검색해서 등록" 결과 한 줄 — previewName은 실제 등록 시 쓰일 상품명 미리보기. */
export interface RegistrableSearchResult {
  candidate:         RegistrationCandidate;
  previewName:       string;
  alreadyRegistered: boolean;
}

/** 등록 후보의 고유 키 — 확장팩 프리뷰/검색 결과/대기 목록/테이블 rowKey가 공유한다. */
export const registrationCode = (r: RegistrableSearchResult): string => r.candidate.shopProductCode;

/** 카드 표시명 — 홀로라이브만 name이 일본어라 nameKr을 우선한다(다른 게임은 nameKr=null). */
export const candidateName = (r: RegistrableSearchResult): string =>
  r.candidate.nameKr || r.candidate.name;

/** 확장팩 표시명 — 위와 같은 이유로 expansionNameKr 우선. */
export const candidateExpansionName = (r: RegistrableSearchResult): string =>
  r.candidate.expansionNameKr || r.candidate.expansionName;